import { useState } from 'react';
import { Bike, Map, Wallet, ChevronRight, Download } from 'lucide-react';

interface OnboardingScreenProps {
  onComplete: () => void;
  onSkip: () => void;
}

export function OnboardingScreen({ onComplete, onSkip }: OnboardingScreenProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      icon: Bike,
      title: 'Deliver faster, earn more',
      description: 'Join thousands of riders making money on their own schedule. Flexible hours, instant job alerts.',
      color: '#E0001B'
    },
    {
      icon: Map,
      title: 'Smart route & instant jobs',
      description: 'Get optimized routes and real-time job notifications. Navigate efficiently with built-in GPS.',
      color: '#00B277'
    },
    {
      icon: Wallet,
      title: 'Safe payouts & daily settlements',
      description: 'Track earnings in real-time. Fast withdrawals with multiple payout options.',
      color: '#FFB020'
    }
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };

  const slide = slides[currentSlide];
  const Icon = slide.icon;

  return (
    <div className="absolute inset-0 bg-white flex flex-col">
      {/* Skip Button */}
      <div className="p-4 flex justify-end">
        <button
          onClick={onSkip}
          className="text-[14px] text-[#6B6B6B] hover:text-[#111111]"
        >
          Skip
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 pb-16">
        <div
          className="w-32 h-32 rounded-full flex items-center justify-center mb-8 transition-all"
          style={{ backgroundColor: `${slide.color}15` }}
        >
          <Icon className="w-20 h-20" style={{ color: slide.color }} />
        </div>

        <h1 className="text-[28px] text-center text-[#111111] mb-4 max-w-sm">
          {slide.title}
        </h1>
        
        <p className="text-center text-[#6B6B6B] max-w-md">
          {slide.description}
        </p>
      </div>

      {/* Bottom Section */}
      <div className="px-8 pb-8">
        {/* Dots */}
        <div className="flex justify-center gap-2 mb-6">
          {slides.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide
                  ? 'w-8 bg-[#E0001B]'
                  : 'w-2 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Button */}
        <button
          onClick={handleNext}
          className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm flex items-center justify-center gap-2"
        >
          {currentSlide < slides.length - 1 ? 'Next' : 'Get Started'}
          <ChevronRight className="w-5 h-5" />
        </button>

        <div className="mt-4">
          <a
            href="https://go7.ng/api/download-rider.php"
            download="go7-rider.apk"
            className="w-full h-11 rounded-[12px] border border-[#E0001B] text-[#E0001B] hover:bg-red-50 transition-colors flex items-center justify-center gap-2 text-[14px] font-medium"
          >
            <Download className="w-4 h-4" />
            Download Android App
          </a>
          <p className="mt-2 text-[11px] leading-4 text-gray-500 text-center">
            To install: open the downloaded file, allow &quot;Install unknown apps&quot;
            for your browser when prompted, then tap Install.
          </p>
        </div>
      </div>
    </div>
  );
}
